import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { paidAccessApi } from "@/lib/api/paid-access";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { PaidAccessEntitlementRows } from "@/components/catalog/PaidAccessEntitlementRows";
import { useRevokeEntitlement } from "@/components/catalog/useRevokeEntitlement";
import { paidResourcePageBreadcrumbs } from "./paid-resource-page-breadcrumbs";
import { usePaidResourceFilterTitle } from "./usePaidResourceFilterTitle";

export default function PaidResourceEntitlementsPage() {
  const [searchParams] = useSearchParams();
  const resourceId = searchParams.get("resourceId") ?? undefined;
  const resourceTitle = usePaidResourceFilterTitle(resourceId);
  const [rows, setRows] = useState<Awaited<ReturnType<typeof paidAccessApi.listEntitlements>>>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    paidAccessApi
      .listEntitlements(resourceId)
      .then(setRows)
      .finally(() => setLoading(false));
  }, [resourceId]);

  useEffect(() => {
    load();
  }, [load]);

  const { revoke, revokingId, ConfirmDialogComponent } = useRevokeEntitlement(load);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Paid resource entitlements"
        description={
          resourceId
            ? "Access grants for the selected digital resource."
            : "Active and historical access grants for paid digital resources."
        }
        titleSize="md"
        showSearch={false}
        breadcrumbItems={paidResourcePageBreadcrumbs(
          resourceId ? "Entitlements" : "Paid resource entitlements",
          resourceId,
          resourceTitle
        )}
      />
      {resourceId && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Filtered by resource.</span>
          <Button variant="link" className="h-auto p-0" asChild>
            <Link to="/catalog/paid-entitlements">Show all resources</Link>
          </Button>
          <Button variant="link" className="h-auto p-0" asChild>
            <Link to={`/catalog/digital/${resourceId}`}>Open resource</Link>
          </Button>
        </div>
      )}
      {loading && <LoadingSpinner />}
      {!loading && rows.length === 0 && (
        <Card className="border border-gray-200">
          <CardContent className="p-0">
            <EmptyState
              icon={<KeyRound className="h-6 w-6 text-slate-400" />}
              title="No entitlements yet"
              description={
                resourceId
                  ? "After patrons complete payment, their access grants will be listed here."
                  : "Active and expired access grants for paid digital resources will appear here."
              }
            />
          </CardContent>
        </Card>
      )}
      {!loading && rows.length > 0 && (
        <PaidAccessEntitlementRows
          rows={rows}
          revokingId={revokingId}
          onRevoke={revoke}
          showResourceTitle={!resourceId}
        />
      )}
      <ConfirmDialogComponent />
    </div>
  );
}
